import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import { AuthProvider } from "./auth/AuthProvider";
import { loadConfig, type AppConfig } from "./config";
import { loadF7 } from "./f7";
import "./styles.css";

/**
 * Mount FIRST, fetch config second.
 *
 * The previous version awaited loadConfig() before calling createRoot, so the
 * browser showed a blank white page for the whole round trip — nothing painted,
 * no spinner, no indication anything was happening. Rendering a shell first
 * costs nothing and turns ~600ms of "is it broken?" into visible progress.
 *
 * The config still has to arrive before the OIDC provider is constructed: it
 * needs the authority and client id up front, and a provider that mounted with
 * placeholders and re-rendered would start an auth flow against the wrong
 * issuer.
 */
function Boot() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The F7 bundle is a prerequisite for the first real paint and does not
    // depend on the config, so the two run concurrently. No F7 component may
    // render before window.F7 exists.
    Promise.all([loadConfig(), loadF7()])
      .then(([cfg]) => setConfig(cfg))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) {
    return (
      <main className="center">
        <h1>Configuration error</h1>
        <pre className="error">{error}</pre>
      </main>
    );
  }

  if (!config) {
    return (
      <main className="center">
        <h1>Foodics AI Admin</h1>
        <p className="muted">Loading…</p>
      </main>
    );
  }

  return (
    <AuthProvider config={config}>
      <App config={config} />
    </AuthProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Boot />
  </StrictMode>,
);
